import React, { useState, useEffect } from 'react';
import { Box, styled, Typography, Menu, MenuItem, Divider, ListItemIcon, ListItemText } from '@mui/material';
import { useThemeToggle } from '../ThemeToggleProvider';
import { useNavigate } from 'react-router-dom';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import axios from 'axios';
import { BASE_PATH } from '../utils/basePath';
import { BRAND_TABLE_TEXT_COLOR } from '../styles/tableColors';

interface BorrowerProfile {
  name: string;
  email: string;
  phone?: string;
  type?: string;
  address?: string;
  mobilePhone?: string;
  /** Logged-in portal Contact name (same as Profile Settings "Name") */
  primaryContact?: string;
  contactId?: string;
}

// Styled components
const HeaderBar = styled(Box)(({ theme }) => ({
  width: '100%', // ensures full width, controlled externally
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  background: theme.palette.mode === 'dark' ? '#000' : '#fff',
  borderBottom: `1.5px solid ${theme.palette.divider}`,
  height: 100,
  boxSizing: 'border-box',
  position: 'relative',
  zIndex: 10,
  gap: theme.spacing(1),
}));

const HeaderContent = styled(Box)(() => ({
  width: '100%',
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 48px',
  height: '100px',
  boxSizing: 'border-box',
}));

const LeftSection = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  gap: 24,
  minWidth: 0,
}));

const LogoBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  gap: 0,
}));

const LogoImg = styled('img')(() => ({
  height: 100,
  width: 'auto',
  marginBottom: 2,
}));

const NavText = styled(Typography)(({ theme }) => ({
  color: BRAND_TABLE_TEXT_COLOR,
  fontWeight: 400,
  fontSize: 18,
  marginLeft: theme.spacing(4),
  fontFamily: "'Libre Franklin', sans-serif",
  cursor: 'pointer',
  '&:hover': {
    color: BRAND_TABLE_TEXT_COLOR,
  },
}));

const RightSection = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  justifyContent: 'flex-end',
  gap: 24,
  minWidth: 0,
}));

const ThemeToggleBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  background: theme.palette.mode === 'dark' ? '#323232' : '#eee',
  borderRadius: 20,
  padding: '2px 4px',
}));

const ThemeButton = styled('button')<{ selected: boolean }>(({ theme, selected }) => ({
  background: selected
    ? (theme.palette.mode === 'dark' ? '#000' : '#fff')
    : 'transparent',
  color: selected
    ? theme.palette.text.primary
    : theme.palette.mode === 'dark'
      ? 'rgba(255,255,255,0.6)'
      : 'rgba(0,0,0,0.5)',
  border: 'none',
  borderRadius: 16,
  padding: '6px 16px',
  cursor: 'pointer',
  fontWeight: selected ? 700 : 400,
  fontSize: 14,
  fontFamily: "'Libre Franklin', sans-serif",
  transition: 'background 0.3s, color 0.3s',
}));


const ProfileMenuBox = styled(Box)(({ theme }) => ({
  background: theme.palette.mode === 'dark' ? '#181818' : '#fff',
  color: theme.palette.mode === 'dark' ? '#fff' : '#232228',
  borderRadius: 20,
  minWidth: 320,
  boxShadow: theme.palette.mode === 'dark' ? '0 4px 32px #0008' : '0 4px 32px #0002',
  padding: 0,
  overflow: 'hidden',
}));





const ProfileMenuName = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: 20,
  color: theme.palette.mode === 'dark' ? '#fff' : '#232228',
}));

const ProfileMenuEmail = styled(Typography)(({ theme }) => ({
  fontSize: 15,
  color: theme.palette.mode === 'dark' ? '#aaa' : '#888',
  marginTop: 2,
}));

// --------- Profile Menu Component ----------
const ProfileMenu: React.FC<{
  anchorEl: null | HTMLElement;
  open: boolean;
  onClose: () => void;
  onSignOut: () => Promise<void>;
  profile: BorrowerProfile | null;
}> = ({ anchorEl, open, onClose, onSignOut, profile }) => {
  const navigate = useNavigate();

  const handleFaqClick = () => {
    navigate('/faq');
    onClose();
  };

  const handleProfileSettingsClick = () => {
    navigate('/profileSettings');
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          background: 'none',
          boxShadow: 'none',
          mt: 1.5,
          borderRadius: 3,
          minWidth: 340,
          p: 0,
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      MenuListProps={{ sx: { p: 0 } }}
    >
      <ProfileMenuBox>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, px: 1 }}>
          <Box
            sx={{
              width: 45,
              height: 45,
              borderRadius: '50%',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <AccountCircleIcon sx={{ fontSize: 60, color: '#98773b' }} />
          </Box>
          <Box>
            <ProfileMenuName>
              {profile?.primaryContact?.trim() || profile?.name || ''}
            </ProfileMenuName>
            <ProfileMenuEmail>{profile?.email}</ProfileMenuEmail>
          </Box>
        </Box>
        <Divider sx={{ my: 2, borderColor: '#444' }} />
        <MenuItem
          sx={{ color: '#98773b', fontWeight: 600, fontSize: 17, py: 1.5 }}
          onClick={handleProfileSettingsClick}
        >
          <ListItemIcon sx={{ color: '#98773b', minWidth: 36 }}>
            <SettingsOutlinedIcon fontSize="medium" />
          </ListItemIcon>
          <ListItemText primary="Profile Settings" />
        </MenuItem>
        <MenuItem sx={{ color: '#fff', fontWeight: 500, fontSize: 16, py: 1.5 }} onClick={handleFaqClick}>
          <ListItemIcon sx={{ color: '#bbb', minWidth: 36 }}>
            <HelpOutlineIcon fontSize="medium" />
          </ListItemIcon>
          <ListItemText primary="FAQs" />
        </MenuItem>
        <Divider sx={{ my: 2, borderColor: '#444' }} />
        <MenuItem 
          sx={{ 
            color: '#fff', 
            fontWeight: 500, 
            fontSize: 17, 
            py: 2, 
            borderRadius: '0 0 20px 20px', 
            '&:hover': { 
              background: (theme) => theme.palette.mode === 'dark' ? '#232228' : '#f5f5f5'
            } 
          }}
          onClick={onSignOut}
        >
          <ListItemIcon sx={{ color: '#04173f', minWidth: 36 }}>
            <LogoutOutlinedIcon fontSize="medium" />
          </ListItemIcon>
          <ListItemText primary="Sign Out" />
        </MenuItem>
      </ProfileMenuBox>
    </Menu>
  );
};

// Main Header Component
const Header: React.FC = () => {
  const { mode, toggleTheme } = useThemeToggle();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [profile, setProfile] = useState<BorrowerProfile | null>(null);
  const [, setLoading] = useState(true);

  const logoSrc = mode === 'light' ? `${BASE_PATH}/logos/logo_dark.png` : `${BASE_PATH}/logos/logo.png`;

  // Fetch user info on component mount
  useEffect(() => {
    axios
      .get(`/api/borrower-profile`, { withCredentials: true })
      .then((res) => setProfile(res.data))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, []);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };
  const handleMenuClose = () => setMenuAnchor(null);

  const handleLoansClick = () => {
    navigate('/loans');
  };

 
const handleSignOut = async () => {
  try {
    // Call backend logout endpoint to revoke token and destroy session FIRST
    await fetch(`${BASE_PATH}/api/logout`, {
      method: "GET",
      credentials: "include",
    });
    
    // Clear client-side state after server logout
    localStorage.clear();
    sessionStorage.clear();
    if ("caches" in window) {
      caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
    }
    
    // Clear all cookies
    document.cookie.split(";").forEach((c) => {
      document.cookie = c
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
    
    // Redirect directly to Salesforce OAuth login (SF_LOGIN_URL via backend)
    window.location.href = `${BASE_PATH}/api/salesforce/salesforce-login-oauth`;
  } catch {
    // On error, still navigate to login
    window.location.href = `${BASE_PATH}/login`;
  }
};

  return (
    <HeaderBar>
      <HeaderContent>
        <LeftSection>
          <LogoBox>
            <LogoImg src={logoSrc} alt="Momenta Finance Logo" />
          </LogoBox>
          <NavText onClick={handleLoansClick}>Loans</NavText>
        </LeftSection>
        <RightSection>
          <ThemeToggleBox sx={{ display: 'none' }}>
            <ThemeButton
              selected={mode === 'dark'}
              onClick={() => mode === 'light' && toggleTheme()}
            >
              Dark
            </ThemeButton>
            <ThemeButton
              selected={mode === 'light'}
              onClick={() => mode === 'dark' && toggleTheme()}
            >
              Light
            </ThemeButton>
          </ThemeToggleBox>
          <Box
            sx={{
              width: 45,
              height: 45,
              borderRadius: '50%',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            onClick={handleProfileClick}
          >
            <AccountCircleIcon sx={{ fontSize: 60, color: '#98773b' }} />
          </Box>
          <ProfileMenu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose} onSignOut={handleSignOut} profile={profile} />
        </RightSection>
      </HeaderContent>
    </HeaderBar>
  );
};

export default Header;